/* eslint-disable sonarjs/no-duplicate-string */
import { cn } from '@lib/utils'
import { createElement, type ReactNode } from 'react'

/**
 * DESIGN SYSTEM NOTES
 * ------------------------------------------------------
 * MOBILE  -> aka Default | < 640
 * TABLET  -> SM | 640 -> 1023
 * DESKTOP -> LG | >= 1024
 * ------------------------------------------------------
 *              MOBILE        TABLET              DESKTOP
 * -- FONT SIZE : LINE HEIGHT
 * H1           60 : 70       70 : 82             80 : 92
 * H2           55 : 65       60 : 70             60 : 70
 * H3           48 : 60       48 : 60             48 : 60
 * H4           40 : 50       40 : 50             40 : 50
 * body-large   30 : 38       30 : 38             30 : 38
 * body         20 : 28       20 : 28             20 : 28
 * body-small   16 : 24       16 : 24             16 : 24
 * -- LINE HEIGHT
 * H1           70            82                  92
 * H2           65            70                  70
 * H3           60            60                  60
 * H4           50            50                  50
 * body-large   38            38                  38
 * body         28            28                  28
 * body-small   24            24                  24
 */

// type BreakpointSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
// type DesignSystemSize = 'mobile' | 'tablet' | 'desktop'
// type OverrideTextStylesMap = Partial<
//   Record<BreakpointSize | DesignSystemSize, TextStyle>
// >

type AllowedElements =
  | 'p'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'span'

export type TypographyName =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'body-large'
  | 'body'
  | 'body-small'

type FontWeight =
  | 'font-thin'
  | 'font-extralight'
  | 'font-light'
  | 'font-normal'
  | 'font-medium'
  | 'font-semibold'
  | 'font-bold'
  | 'font-extrabold'
  | 'font-black'
const fontWeightMap: Record<FontWeight, string> = {
  'font-thin': '!font-thin',
  'font-extralight': '!font-extralight',
  'font-light': '!font-light',
  'font-normal': '!font-normal',
  'font-medium': '!font-medium',
  'font-semibold': '!font-semibold',
  'font-bold': '!font-bold',
  'font-extrabold': '!font-extrabold',
  'font-black': '!font-black',
}

type TextStyle = string | null // CSS Classname for style

type TypographyMap = Record<TypographyName, TextStyle>

const typographyMap: TypographyMap = {
  h1: 'font-bold text-h1',
  h2: 'font-bold text-h2',
  h3: 'font-bold text-h3',
  h4: 'font-bold text-h4',
  'body-large': 'font-normal text-body-large',
  body: 'font-normal text-body',
  'body-small': 'font-normal text-body-small',
}

export const Typography = ({
  as,
  variant,
  overrideWeight,
  className: propClassName,
  children,
  ...restProps
}: {
  as: AllowedElements
  variant: TypographyName
  overrideWeight?: FontWeight
  className?: string
  children: ReactNode
}) => {
  const textStyleClassName = typographyMap[variant]

  const className = cn(
    textStyleClassName,
    overrideWeight == null ? null : fontWeightMap[overrideWeight],
    propClassName,
  )

  return createElement(as, { className, ...restProps }, children)
}
